import moment from 'moment';
import { DateRangePickerProps } from '@amzn/awsui-components-react';

const isBeforeDate = (start: string, end: string): boolean => {
    return moment(end).isBefore(moment(start));
};

export const isValidRange: DateRangePickerProps.ValidationFunction = (
    dateRange
): DateRangePickerProps.ValidationResult => {
    if ('absolute' === dateRange.type) {
        const [startDate] = dateRange.startDate.split('T');
        const [endDate] = dateRange.endDate.split('T');

        if (!startDate || !endDate) {
            return {
                valid: false as const,
                errorMessage:
                    'The selected date range is incomplete. Select a start and end date for the date range.',
            };
        }

        if (
            moment(dateRange.startDate).isAfter(moment()) ||
            moment(dateRange.endDate).isAfter(moment())
        ) {
            return {
                valid: false as const,
                errorMessage:
                    'Either of start date or end date cannot be future date.',
            };
        }

        if (isBeforeDate(dateRange.startDate, dateRange.endDate)) {
            return {
                valid: false as const,
                errorMessage: 'The end date must be greater than the start date.',
            };
        }
    } else if ('relative' === dateRange.type) {
        if (isNaN(dateRange.amount)) {
            return {
                valid: false as const,
                errorMessage:
                    'The selected date range is incomplete. Specify a duration for the date range.',
            };
        }
    }

    return { valid: true as const };
};


interface TimezoneOption {
    value: string;
    code: string;
}

/**
 * Converts a timestamp to a formatted string in the specified timezone
 * @param timestamp - Unix timestamp (can be string or number)
 * @param tzOption - Timezone option object containing value and code
 * @returns Formatted date string with timezone
 */
export const convertTZ = (
    timestamp: string | number,
    tzOption: TimezoneOption
): string => {
    const tz = tzOption.value;
    const normalizedTimestamp = typeof timestamp === 'string' 
        ? parseInt(timestamp, 10) 
        : timestamp;

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        hour12: false,
        timeZoneName: 'short',
    };

    if (tz === 'Browser Timezone') {
        return new Date(normalizedTimestamp).toLocaleString('en-US', dateFormatOptions);
    }

    return `${new Date(normalizedTimestamp).toLocaleString('en-US', {
        ...dateFormatOptions,
        timeZone: tz,
    })} ${tzOption.code}`;
};
